"use client";

import type { StudentRating } from "@/api/query/student-rating";
import Image from "next/image";
import { MdEmail } from "react-icons/md";
import { BsWhatsapp } from "react-icons/bs";
import { useEffect, useState } from "react";
import { StarRating } from "./StarRating";
import { CoursesModal } from "./CourseModal";
import { getStudentPic } from "@/api/query/hall-of-fame";
import { StudentInitials } from "../hall-of-fame/StudentInitials";

interface StudentCardProps {
  student: StudentRating;
  rank: number;
}

export function StudentCard({ student, rank }: StudentCardProps) {
  const studentName = `${student.student.firstName} ${student.student.lastName}`;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [picUrl, setPicUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const studentId = student.student.id;

  useEffect(() => {
    const fetchPic = async () => {
      setLoading(true);
      const url = await getStudentPic(studentId);
      setPicUrl(url);
      setLoading(false);
    };

    fetchPic();
  }, [studentId]);

  return (
    <>
      <div className="flex flex-col bg-white border border-[#e5e5e5] rounded-lg hover:shadow-lg transition-shadow relative">
        {/* Contact Icons - Top Left */}
        <div className="absolute top-4 left-4 flex flex-col gap-3 text-base">
          <a
            target="blank"
            href={`mailto:${student.student.email}`}
            title="Email"
            className="text-[#4054ed]"
          >
            <MdEmail />
          </a>
          <a
            target="blank"
            href={`https://wa.me/${student.student.phoneNumber}`}
            title="WhatsApp"
            className="text-[#25D366]"
          >
            <BsWhatsapp />
          </a>
        </div>

        {/* Centered Content */}
        <div className="flex flex-col items-center text-center">
          {/* Rank Badge */}
          <section className="p-3 h-full">
            {rank <= 3 && (
              <div className="mb-4 inline-flex items-center justify-center w-8 h-8 rounded-full bg-yellow-400 text-white font-bold text-sm">
                #{rank}
              </div>
            )}

            {/* Avatar */}
            <div className="mb-4 relative w-20 h-20  bg-[#21205A] text-center rounded-full overflow-hidden border-2 border-gray-200">
              {loading ? (
                <div className="w-full h-full bg-[#21205A] animate-pulse" />
              ) : picUrl ? (
                <Image
                  src={picUrl || "/placeholder.svg"}
                  alt={student.student.firstName}
                  width={200}
                  height={200}
                  className="w-full h-full object-cover"
                />
              ) : (
           
                <div className="  h-full w-full  text-center flex justify-center items-center">
                  <StudentInitials
                    firstName={student.student.firstName}
                    lastName={student.student.lastName}
                    size="lg"
                  />
                </div>
              )}
            </div>
          </section>
          <section className="w-full bg-[#21205A] h-full text-[#FFF] p-3 flex flex-col items-center justify-center rounded-b-lg">
            {/* Name */}
            <h3 className="font-semibold text-base mb-2 line-clamp-2 uppercase">
              {studentName}
            </h3>

            {/* Certificates - Clickable */}
            <button
              onClick={() => setIsModalOpen(true)}
              title="click to view certificates"
              className="mb-4 text-sm text-[#FFF]  transition-colors cursor-pointer"
            >
              <p className="font-medium text-[#FFF]">
                {student.totalPassedPaidCourses}
              </p>
              <p className="text-base">Certificates</p>
              <p className="text-sm opacity-65">Click to view certificates</p>
            </button>

            {/* Star Rating */}
            <StarRating avgPerMonth={student.avgPerMonth} />
          </section>
        </div>
      </div>

      <CoursesModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        studentId={student.student.id}
        studentName={studentName}
        totalCourses={student.totalPassedPaidCourses}
      />
    </>
  );
}
